import styled, { css } from 'styled-components'
import {
	backgroundOrange,
	darkOrange,
	orange,
	gold,
	green,
	success,
	danger,
	black,
	textMuted,
	mutedGrey,
	warmTint,
	border1,
} from '@/theme/colors'
import { radii, media } from '@/theme/tokens'
import { fontSizes, text, leading } from '@/theme/typography'
import { Button } from '@components/Button'
import { innerCardSurface, tintOnHover } from '@components/Card'
import { Pill } from '@components/Pill'

export const SectionRoot = styled.div`
	display: flex;
	flex-direction: column;
	flex: 1;
	min-height: 0;
`

export const SectionHeader = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 12px;
	padding-bottom: 14px;
	border-bottom: 1px solid ${warmTint};
	margin-bottom: 14px;
	flex-shrink: 0;
	flex-wrap: wrap;
`

export const SectionTitle = styled.h3`
	${text.title}
	color: ${darkOrange};
	margin: 0;
`

export const SectionHeaderActions = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
	flex-wrap: wrap;
`

export const SectionBody = styled.div`
	flex: 1;
	min-height: 0;
	overflow-y: auto;
	padding-right: 4px;
	scrollbar-width: none;
	-ms-overflow-style: none;

	&::-webkit-scrollbar {
		display: none;
	}
`

export const HeaderActionButton = styled(Button).attrs({ size: 'sm' as const })`
	white-space: nowrap;
`

export const ExportButton = styled(Button).attrs({ variant: 'secondary' as const, size: 'sm' as const })`
	white-space: nowrap;
`

export const FilterButton = styled(Button).attrs({ variant: 'secondary' as const, size: 'sm' as const })<{ $active?: boolean }>`
	${({ $active }) =>
		$active &&
		css`
			border-color: ${orange};
			background: ${backgroundOrange};
		`}
`

/** The count bubble on the filter button: tighter than a label pill. */
export const FilterCountBadge = styled(Pill)`
	justify-content: center;
	min-width: 16px;
	padding: 0 5px;
`

export const ActivityList = styled.div`
	display: flex;
	flex-direction: column;
	gap: 20px;
`

export const DateGroup = styled.div`
	display: flex;
	flex-direction: column;
	gap: 10px;
`

export const DateHeader = styled.h4`
	${text.eyebrow}
	color: ${gold};
	margin: 0 0 4px 0;
	padding-bottom: 8px;
`

export const ActivityGrid = styled.div`
	display: grid;
	grid-template-columns: repeat(auto-fill, minmax(425px, 1fr));
	gap: 12px;

	${media.phone} {
		grid-template-columns: 1fr;
	}
`

/** $interactive=false renders the read-only (public) card: no pointer, no hover tint. */
export const ActivityCard = styled.div<{ $interactive?: boolean }>`
	${innerCardSurface}
	${({ $interactive = true }) => ($interactive ? tintOnHover : '')}
	padding: 16px 18px;
	display: flex;
	flex-direction: column;
	justify-content: space-between;
	gap: 16px;
	min-width: 0;
	min-height: 112px;
	overflow: hidden;
`

export const ActivityCardTop = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	gap: 8px;
	min-width: 0;
	flex: 1;
`

export const ActivityCardLeft = styled.div`
	display: flex;
	flex-direction: column;
	gap: 2px;
	min-width: 0;
	flex: 1;
`

export const ActivityCardName = styled.span`
	font-size: ${fontSizes.base};
	font-weight: 700;
	color: ${black};
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
`

export const ActivityCardThumbnail = styled.img`
	width: 40px;
	height: 40px;
	border-radius: ${radii.small};
	object-fit: cover;
	flex-shrink: 0;
	border: 1px solid ${warmTint};
`

export const ActivityCardBottom = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	border-top: 1px solid ${border1};
	padding-top: 12px;
	gap: 8px;
	min-width: 0;
`

export const PayerLine = styled.div`
	display: flex;
	align-items: center;
	gap: 6px;
	min-width: 0;
	flex: 1;
	font-size: ${fontSizes.micro};
	font-weight: 500;
	color: ${textMuted};
`

export const PayerName = styled.span<{ $highlight?: boolean }>`
	color: ${({ $highlight }) => ($highlight ? orange : black)};
	font-weight: ${({ $highlight }) => ($highlight ? 700 : 500)};
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	min-width: 0;
`

export const PayerAmount = styled.span`
	color: ${orange};
	font-weight: 700;
	flex-shrink: 0;
`

export const PaidConnector = styled.span`
	flex-shrink: 0;
`

export const PayUpAvatarRow = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
`

export const PayUpArrow = styled.span`
	color: ${green};
	font-size: ${fontSizes.title};
	font-weight: 700;
	line-height: ${leading.none};
`

export const PayUpMoneyIcon = styled.span`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	color: ${green};

	svg {
		width: 28px;
		height: 28px;
	}
`

export const ActivityCardCurrencyCode = styled.span`
	font-size: ${fontSizes.micro};
	font-weight: 500;
	color: ${textMuted};
	margin-left: 4px;
`

export const ActivityCardInvolvement = styled.span<{
	$type: 'lent' | 'borrowed' | 'none' | 'paid' | 'received'
}>`
	font-size: ${fontSizes.micro};
	font-weight: 500;
	color: ${({ $type }) => {
		switch ($type) {
			case 'lent':
			case 'received':
				return success
			case 'borrowed':
			case 'paid':
				return danger
			case 'none':
				return mutedGrey
		}
	}};
`
